import type { InputProps } from './input'
import { Eye, EyeOff } from 'lucide-react'
import * as React from 'react'

import { useTranslation } from 'react-i18next'
import { cn } from '~/lib/utils'
import { Button } from './button'
import { Input } from './input'

export type PasswordInputProps = Omit<InputProps, 'type'>

/**
 * Champ mot de passe avec un bouton pour afficher ce qui est tapé (#237).
 * Les props, `ref` comprise, vont au `<input>` : `FormControl` y pose `id` et `aria-*`.
 * Le mot de passe repasse en masqué à la soumission du formulaire qui l'entoure.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const { t } = useTranslation()
    const [visible, setVisible] = React.useState(false)
    const wrapperRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      const form = wrapperRef.current?.closest('form')
      if (!form) return
      const hide = () => setVisible(false)
      form.addEventListener('submit', hide)
      return () => form.removeEventListener('submit', hide)
    }, [])

    const Icon = visible ? EyeOff : Eye

    return (
      <div ref={wrapperRef} className="relative">
        <Input ref={ref} type={visible ? 'text' : 'password'} className={cn('pr-12', className)} disabled={disabled} {...props} />
        <Button
          type="button"
          disabled={disabled}
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 size-9 -translate-y-1/2 text-muted-foreground"
          aria-pressed={visible}
          aria-label={t('password.show')}
          onClick={() => setVisible(v => !v)}
        >
          <Icon className="size-5" aria-hidden />
        </Button>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'

export { PasswordInput }
